import { useState } from 'react';
import type { StepProps } from '../OBWWizard';
// ⭐ ONE DEFINITION, SHARED WITH THE SIGN-UP MODAL. Moved to lib/ on 13 Aug 2026
// (ADR-105 D1 Am.2): the modal now captures the domain and this step confirms
// it, so a second copy of the normaliser is a guaranteed future disagreement.
import { normaliseDomainInput } from '../../lib/domain-normalise';

// Phase 5c.5 (ADR-030 D2 Step 5) — domain field + verify-status indicator
// + consent block (obw-auth-box). Status indicator transitions:
// idle → checking → verified | failed. Real DNS verification happens server-
// side at OBW persistence; client status is presentation-only.
//
// OBW UAT r2 #3 — this is the SHARED domain step used by every journey
// (SOW / BOW / partner / manual-onboard). It now accepts a BARE domain
// (`hvt.ai`) — no `https://` or `www.` required. The input is normalised
// internally (trim, lowercase, add protocol, strip `www.`) before use, mirroring
// the server-side `normaliseDomain` in lambdas/portal-api/src/bow.ts so client
// and server agree on the canonical form. The fix lands once here and benefits
// every journey (component-catalogue propagation discipline, ADR-069).

type VerifyStatus = 'idle' | 'checking' | 'verified' | 'failed';


export function StepDomainVerify({ data, onNext, isRequired, actor, presentation }: StepProps) {
  // ADR-073 Am.5 §A5.6 — condensed for the public modal's frame (no scrolling); same consent words.
  const compact = presentation === 'embedded';
  const [domainUrl, setDomainUrl] = useState<string>((data?.domainUrl as string) ?? '');
  const [consented, setConsented] = useState<boolean>((data?.consented as boolean) ?? false);
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>(
    domainUrl ? 'verified' : 'idle',
  );
  const [error, setError] = useState('');

  function handleUrlBlur() {
    if (!domainUrl.trim()) { setVerifyStatus('idle'); return; }
    // Accepts a bare domain — valid if it normalises to a plausible hostname.
    setVerifyStatus(normaliseDomainInput(domainUrl) ? 'verified' : 'failed');
  }

  function handleNext() {
    setError('');
    const normalised = normaliseDomainInput(domainUrl);
    if (isRequired) {
      if (!domainUrl.trim()) { setError('Please enter your domain.'); return; }
      if (!normalised) { setError('Please enter a valid domain, e.g. yourbusiness.com.'); return; }
      if (!consented) { setError('You must confirm authorisation to proceed.'); return; }
    }
    // Pass the canonical form forward (or the trimmed input for an empty optional
    // step). domainHostname is the bare host for downstream uniqueness display.
    onNext({
      domainUrl: normalised ? normalised.url : domainUrl.trim(),
      domainHostname: normalised?.hostname ?? '',
      consented,
    });
  }

  const consentLabel =
    actor === 'nexa-admin'
      ? "I confirm the subscriber has authorised connection of this domain to Nexanomics."
      : 'I confirm I own or have authority over this domain and authorise Nexanomics to monitor it.';

  const intro =
    actor === 'nexa-admin'
      ? "Enter the subscriber's primary domain. By proceeding, you confirm authorisation from the subscriber to connect this domain to Nexanomics."
      : compact
        ? 'Confirm the website this subscription is for. We never modify or proxy your site.'
        : 'Enter your primary domain below. We monitor trust signals and deliverability — we never modify or proxy your site.';

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Domain Authorization</h2>
      <p className="obw-subtitle">{intro}</p>

      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-domain-url">Domain</label>
        <input
          id="obw-domain-url"
          className="obw-form-input"
          type="text"
          inputMode="url"
          value={domainUrl}
          onChange={(e) => { setDomainUrl(e.target.value); setVerifyStatus('idle'); }}
          onBlur={handleUrlBlur}
          placeholder="yourbusiness.com"
          autoComplete="url"
        />
        <p className="obw-form-hint" aria-live="polite">
          {verifyStatus === 'idle' && (compact ? 'The domain only — no https:// or www.' : 'Enter the domain only — no need for https:// or www. We\'ll check the format when you tab out.')}
          {verifyStatus === 'checking' && 'Checking…'}
          {verifyStatus === 'verified' && (
            <span style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Looks good. Full DNS verification runs at activation.</span>
          )}
          {verifyStatus === 'failed' && (
            <span style={{ color: 'var(--danger)', fontWeight: 600 }}>✗ That doesn't look like a valid domain.</span>
          )}
        </p>
      </div>

      <div className="obw-auth-box">
        <label className="obw-auth-check">
          <input
            type="checkbox"
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
          />
          <span>{consentLabel}</span>
        </label>
        <p className="obw-auth-note">
          {compact
            ? 'Required by the Nexanomics Terms of Service. We only read the events your snippet sends.'
            : 'Authorisation is required by the Nexanomics Terms of Service. We never modify or proxy your site — we only ingest beacon events emitted by the snippet you install.'}
        </p>
      </div>

      {error && <div className="portal-wizard-error" role="alert">{error}</div>}

      <div className="portal-wizard-nav">
        <button type="button" className="obw-btn-primary" onClick={handleNext}>
          {isRequired ? 'Authorize domain' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
